import React, { useEffect, useState } from 'react';
import { ErrorInput } from '@fx-ui/jdy-design-biz';
import {
    getCodeByValue,
    getPhoneByValue,
    hasFunctionPlatformPhone,
    localStorageManager
} from '~/utils';
import Intl from '@fx-ui/intl';
import classNames from 'classnames';
import { LOCAL_STORAGE_MIGRATE } from '~/constants';
import { AreaCode } from '@fx-ui/platform-sdk';

interface ILoginNameProps {
    className?: string;
    height?: string | number;
    value?: string;
    phoneDelimiter?: string;
    waterMark?: string;
    disabled?: boolean;
    nameValid?: boolean;
    autoSelect?: boolean;
    tabIndex?: number;
    needCache?: boolean;
    errorMsg?: string;
    errorTrigger?: 'blur' | 'focus';
    onBlur?: (value: string, hasPhoneCode: boolean) => void;
    onChange?: (value: string, hasPhoneCode: boolean) => void;
}

// 用户连续输入7个数字就将其识别为手机号
const PHONE_NUMBER_REGEXP = '^(\\d{7,})$';
// 是否为带区号的手机号，注意这里的手机号部分至少要7位
const PHONE_WITH_AREA_CODE_REGEXP = '^\\+\\d{1,4}-\\d{7,}$';

/**
 * 根据不同的正则表达式来检验是否为有效的手机号
 *
 * @param value 用户输入的值
 * @param reg 正则校验表达式
 */
const isValidPhoneValue = (value: string, reg: string) => {
    return new RegExp(reg).test(value) && hasFunctionPlatformPhone();
};

/**
 * 用户名输入组件，有两种输入模式：
 * 1、手机号
 * 2、邮箱或用户名
 *
 * @param props ILoginNameProps
 */
const LoginName: React.FunctionComponent<ILoginNameProps> = (props) => {
    const {
        className,
        height = 40,
        value = '',
        phoneDelimiter = '-',
        disabled = false,
        autoSelect = false,
        nameValid = true,
        tabIndex,
        waterMark = hasFunctionPlatformPhone()
            ? Intl.get('account.signin.user.placeholder')
            : Intl.get('email'),
        needCache = false,
        errorMsg = '',
        errorTrigger = 'blur',
        onBlur,
        onChange
    } = props;

    const getLoginName = () => {
        if (value) {
            const isInterPhoneMode = isValidPhoneValue(value, PHONE_WITH_AREA_CODE_REGEXP);
            if (isInterPhoneMode) {
                return getPhoneByValue(value, phoneDelimiter);
            }
            return value;
        }
        return '';
    };

    const [nameValue, setNameValue] = useState<string>(() => {
        return getLoginName();
    });

    const [areaCode, setAreaCode] = useState<string>(() => {
        return getCodeByValue(value, needCache, phoneDelimiter);
    });

    const [showPhoneCode, setShowPhoneCode] = useState<boolean>(false);

    useEffect(() => {
        const isInterPhoneMode = isValidPhoneValue(value, PHONE_WITH_AREA_CODE_REGEXP);
        setNameValue(getLoginName());
        setShowPhoneCode(isInterPhoneMode);
        if (isInterPhoneMode) {
            setAreaCode(getCodeByValue(value, needCache, phoneDelimiter));
        }
    }, [value]);

    const handleAreaCodeChange = (areaCodeVal: string) => {
        setAreaCode(areaCodeVal);
        needCache && localStorageManager.set(LOCAL_STORAGE_MIGRATE.SIGN_IN_PHONE_AREA, areaCodeVal);
        const value = nameValue ? areaCodeVal + phoneDelimiter + nameValue : '';
        // 兼容之前逻辑，onChange和onBlur同时存在时，只触发onChange
        if (onChange) {
            onChange(value, true);
        } else if (onBlur) {
            // 当只传入onBlur事件时触发onBlur事件
            onBlur(value, true);
        }
    };

    const getBeforeAddon = () => {
        return (
            <AreaCode
                areaCode={areaCode}
                disabled={disabled}
                height={height}
                onAreaCodeChange={handleAreaCodeChange}
            />
        );
    };

    return (
        <div className={classNames('fx-login-name', className)}>
            <ErrorInput
                autoComplete={true}
                errMsg={errorMsg}
                errorTrigger={errorTrigger}
                disabled={disabled}
                autoSelect={autoSelect}
                tabIndex={tabIndex}
                value={nameValue}
                className="name-input"
                placeholder={waterMark}
                inputState={nameValid ? 'normal' : 'error'}
                beforeAddon={showPhoneCode ? getBeforeAddon() : null}
                style={{
                    height: height,
                    width: '100%'
                }}
                onChange={(val) => {
                    const isPhoneMode = isValidPhoneValue(val, PHONE_NUMBER_REGEXP);
                    setNameValue(val);
                    setShowPhoneCode(isPhoneMode);
                    if (onChange) {
                        let phoneValue = val;
                        if (isPhoneMode) {
                            phoneValue = val ? areaCode + phoneDelimiter + val : '';
                        }
                        onChange(phoneValue, isPhoneMode);
                    }
                }}
                onBlur={() => {
                    if (disabled) {
                        return;
                    }
                    if (onBlur) {
                        let value = nameValue;
                        if (showPhoneCode) {
                            value = nameValue ? areaCode + phoneDelimiter + nameValue : '';
                        }
                        onBlur(value, showPhoneCode);
                    }
                }}
            />
        </div>
    );
};

export default LoginName;
